import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import usePage from "./usePage";
import { PageId } from "@/types/page";
import { UserPermissionEnum } from "@/types/user";
import { hasPermission } from "@/utils/user";
import { ROUTER_KEYS } from "@/router/routers";
import { AppDispatch, RootState } from "@/store";
import { getStorage } from "@/utils/storage";
import { setLoginStatus } from "@/store/modules/userReducer";
import { notification } from "antd";
import { t } from "i18next";


const useDefaultPage = () => {
  const navigate = useNavigate()
  const router = useLocation();

  const curUser = useSelector((state: RootState) => state.userReducer.curUser);
  const loginStatus = useSelector((state: RootState) => state.userReducer.loginStatus);
  const stateDispatch: AppDispatch = useDispatch();

  const { pageId } = usePage()

  const authMaps: {
    [key in string]: UserPermissionEnum
  } = {
    [PageId.SALE_NOT_PASS]: UserPermissionEnum.SALE_REVIEW,
    [PageId.SALE_OFFICIAL]: UserPermissionEnum.SALE_OK_ORDER,
    [PageId.SALE_CUSTOMER]: UserPermissionEnum.SALE_CUSTOMER,
    [PageId.SALES_PRODUCT_INFO]: UserPermissionEnum.SALES_PRODUCT_INFO,
    [PageId.MC_NOT_PASS]: UserPermissionEnum.MC_REQUIREMENT,
    [PageId.MC_PASSED]: UserPermissionEnum.MC_REQUIREMENT,
    [PageId.MC_BE_REJECT]: UserPermissionEnum.MC_REQUIREMENT,
    [PageId.MC_OFFICIAL_PENDING]: UserPermissionEnum.MC_OK_ORDER,
    [PageId.MC_OFFICIAL_DONE]: UserPermissionEnum.MC_OK_ORDER,
    [PageId.MC_OFFICIAL_CLOSED]: UserPermissionEnum.MC_OK_ORDER,
    [PageId.MC_BOM]: UserPermissionEnum.MC_BOM,
    [PageId.MC_REQ_MANAGE]: UserPermissionEnum.REQ_MANAGE,
    [PageId.MC_REQ_M]: UserPermissionEnum.MC_REQ_M,
    [PageId.MATERIAL_INVENTORY_ALERT]: UserPermissionEnum.MATERIAL_INVENTORY_ALERT,
    [PageId.COMMODITY_NOT_PASS]: UserPermissionEnum.CMD_REQUIREMENT,
    [PageId.COMMODITY_PASSED]: UserPermissionEnum.CMD_REQUIREMENT,
    [PageId.COMMODITY_BE_REJECT]: UserPermissionEnum.CMD_REQUIREMENT,
    [PageId.PROD_NOT_PASS]: UserPermissionEnum.PR_REQUIREMENT,
    [PageId.PROD_PASSED]: UserPermissionEnum.PR_REQUIREMENT,
    [PageId.PROD_BE_REJECT]: UserPermissionEnum.PR_REQUIREMENT,
    [PageId.PROD_OFFICIAL_PENDING]: UserPermissionEnum.PR_OK_ORDER,
    [PageId.PROD_OFFICIAL_DONE]: UserPermissionEnum.PR_OK_ORDER,
    [PageId.PROD_OFFICIAL_CLOSED]: UserPermissionEnum.PR_OK_ORDER,
    [PageId.PROD_ENTER_DATA]: UserPermissionEnum.PR_REPORT_ENTRY,
    [PageId.PROD_DATA_MT]: UserPermissionEnum.PR_REPORT_MT,
    [PageId.PROD_MATERIAL_OVER_ISSUE]: UserPermissionEnum.PR_REQUIREMENT,
    [PageId.PUR_NOT_PASS]: UserPermissionEnum.PUR_REQUIREMENT,
    [PageId.PUR_PASSED]: UserPermissionEnum.PUR_REQUIREMENT,
    [PageId.PUR_BE_REJECT]: UserPermissionEnum.PUR_REQUIREMENT,
    [PageId.PUR_ALL_QUTATION]: UserPermissionEnum.PUR_REQUIREMENT,
    [PageId.PUR_OFFICIAL_PENDING]: UserPermissionEnum.PUR_OK_ORDER,
    [PageId.PUR_OFFICIAL_DONE]: UserPermissionEnum.PUR_OK_ORDER,
    [PageId.PUR_OFFICIAL_CLOSED]: UserPermissionEnum.PUR_OK_ORDER,
    [PageId.PUR_SUPPLIER]: UserPermissionEnum.PUR_SUPPLIER,
    [PageId.PUR_DIS_SUPPLIER]: UserPermissionEnum.PUR_SUPPLIER,
    [PageId.PUR_ALL_PASSED]: UserPermissionEnum.PUR_REQUIREMENT_ORDER,
    [PageId.PUR_ORDER_PENDING_PAY]: UserPermissionEnum.PUR_ORDER,
    [PageId.PUR_ORDER_PAYED]: UserPermissionEnum.PUR_ORDER,
    [PageId.PUR_ORDER_DONE]: UserPermissionEnum.PUR_ORDER,
    [PageId.PUR_HELP_ACCEPT]: UserPermissionEnum.PUR_HELP_ACCEPT,
    [PageId.PUR_OM]: UserPermissionEnum.PURCHASE_OPTION_MANUAL,
    [PageId.PUR_MT]: UserPermissionEnum.PURCHASE_MATERIAL_TRACK,
    [PageId.PUR_RT]: UserPermissionEnum.PURCHASE_RETURN,
    [PageId.PUR_INV_ITEM]: UserPermissionEnum.PUR_INV_ITEM,
    [PageId.F_NOT_PASS]: UserPermissionEnum.FINANCE_REQUIREMENT,
    [PageId.F_PASSED]: UserPermissionEnum.FINANCE_REQUIREMENT,
    [PageId.F_BE_REJECT]: UserPermissionEnum.FINANCE_REQUIREMENT,
    [PageId.F_SALE_OFFICIAL]: UserPermissionEnum.FINANCE_SALE_ORDER_FINISH,
    [PageId.F_OFFICIAL_PENDING]: UserPermissionEnum.FINANCE_OK_ORDER,
    [PageId.F_OFFICIAL_DONE]: UserPermissionEnum.FINANCE_OK_ORDER,
    [PageId.F_OFFICIAL_CLOSED]: UserPermissionEnum.FINANCE_OK_ORDER,
    [PageId.WAREHOUSE_INVENTORY]: UserPermissionEnum.STORE_WAREHOUSE_MANAGEMENT,
    [PageId.WAREHOUSE_INVENTORY_HIS]: UserPermissionEnum.STORE_ORDER,
    [PageId.WAREHOUSE_INVENTORY_COUNTING]: UserPermissionEnum.STORE_COUNTING,
    [PageId.WAREHOUSE_INVENTORY_DETAIL]: UserPermissionEnum.WAREHOUSE_INVENTORY_DETAIL,
    [PageId.LOC_INV_DTL]: UserPermissionEnum.LOC_INV_DTL,
    [PageId.WAREHOUSE_IQC]: UserPermissionEnum.WAREHOUSE_IQC,
    [PageId.INSPECTION]: UserPermissionEnum.INSPECTION,
    [PageId.INSPECTION_ITEM]: UserPermissionEnum.INSP_ITEM,
    [PageId.PACKING]: UserPermissionEnum.PACKING,
    [PageId.FAC_INVENTORY]: UserPermissionEnum.FAC_INVENTORY,
    [PageId.DATA_PROD_DATA]: UserPermissionEnum.DATA_ORDER_REPORT,
    [PageId.ORG_MANA]: UserPermissionEnum.ORG_PREVIEW,
    [PageId.MEMBER_DISABLED]: UserPermissionEnum.MEMBER_LIST,
    [PageId.MEMBER_ENABLED]: UserPermissionEnum.MEMBER_LIST,
    [PageId.PRODUCT_CATEGORY]: UserPermissionEnum.PRODUCT_ADD_CATEGORY_LIST,
    [PageId.PRODUCT_LIST]: UserPermissionEnum.PRODUCT_LIST,
    [PageId.APP_LIST]: UserPermissionEnum.APP_LIST,
    [PageId.DATA_MANAGE_SUPPLIER]: UserPermissionEnum.DATA_MANAGE_SUPPLIER,
    [PageId.REIMBURSE_ALL]: UserPermissionEnum.REIMBURSE_ALL,
    [PageId.SHIPMENT_LIST]: UserPermissionEnum.SHIPMENT_LIST,
    [PageId.EQUIPMENT]: UserPermissionEnum.EQUIPMENT_MANAGEMENT,
  }

  useEffect(() => {
    if (router.pathname.startsWith("/s2")) {
      let sid = getStorage("SUPPLIER_ID");
      if (!sid && !router.pathname.includes('/login')) {
        notification.warning({ placement: 'top', message: t("member.aginLogin") })
        setTimeout(() => {
          navigate(ROUTER_KEYS.SUPPLIER_LOGIN)
        }, 300)
      }
    } else {
      if (getStorage("accessToken") && loginStatus) {
        let pathUrl = ["/", "/login"].includes(router.pathname) ? ROUTER_KEYS.WELCOME : router.pathname + router.search;
        navigate(pathUrl, { replace: true });
      } else {
        cleanData()
      }
    }
  }, [loginStatus, getStorage("SUPPLIER_ID")]);

  useEffect(() => {
    if (pageId && loginStatus) {
      if (hasPermission(curUser, [UserPermissionEnum.ADMIN])) return
      const requiredPermission = pageId ? (authMaps[pageId] ?? null) : null;
      const isResolve = requiredPermission && hasPermission(curUser, [requiredPermission]);

      if (!isResolve) {
        navigate(ROUTER_KEYS.WELCOME, { replace: true });
      }
    }
  }, [pageId, loginStatus])

  const cleanData = () => {
    stateDispatch(setLoginStatus(false));
    navigate(ROUTER_KEYS.LOGIN, { replace: true });
  }
}

export default useDefaultPage
